import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Search } from "lucide-react";
import { quotesApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtMoney, fmtDate, statusTone, prettyStatus } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, EmptyState, ErrorState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

function QuoteRow({ x, onOpen }) {
  return (
    <Card className="flex cursor-pointer items-center justify-between gap-3 p-4 transition-shadow hover:shadow-md" onClick={() => onOpen(x.id)} data-testid={`portal-quote-row-${x.id}`}>
      <div className="min-w-0">
        <p className="truncate font-semibold text-slate-900">{x.title || `Quote ${String(x.id).slice(-6)}`}</p>
        <p className="truncate text-sm text-slate-500">{x.building_name || "—"}{x.valid_until ? ` · valid to ${fmtDate(x.valid_until)}` : ""}</p>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <span className="font-semibold text-slate-800">{fmtMoney(x.total)}</span>
        <Badge variant="outline" className={statusTone(x.status)}>{prettyStatus(x.status)}</Badge>
      </div>
    </Card>
  );
}

export default function PortalQuotes() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => quotesApi.list(), []);
  const [q, setQ] = useState("");
  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((x) => `${x.title || ""} ${x.building_name || ""} ${x.reference || ""}`.toLowerCase().includes(s));
  }, [list, q]);

  const pending = filtered.filter((x) => x.status === "sent");
  const others = filtered.filter((x) => x.status !== "sent");
  const open = (id) => navigate(`/portal/quotes/${id}`);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader title="Your quotes" subtitle="Review and approve quotes for your buildings" testId="portal-quotes-header" />
      {loading && <LoadingState label="Loading quotes…" />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search quotes…" className="pl-9" data-testid="portal-quotes-search" />
          </div>
          {filtered.length === 0 ? (
            <EmptyState icon={FileText} title="No quotes yet" message="Quotes raised for your buildings will appear here." />
          ) : (
            <div className="space-y-4">
              {pending.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Awaiting your decision</p>
                  {pending.map((x) => <QuoteRow key={x.id} x={x} onOpen={open} />)}
                </div>
              )}
              {others.length > 0 && (
                <div className="space-y-2">
                  {pending.length > 0 && <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Other quotes</p>}
                  {others.map((x) => <QuoteRow key={x.id} x={x} onOpen={open} />)}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
